import { z } from 'zod';
import { isoDate, uuid } from './common';

/** Whether this workspace has Slack, and which one (PRD 33). */
export const slackState = z.object({
  connected: z.boolean(),
  teamName: z.string().nullable(),
  installedAt: isoDate.nullable(),
  /** Whether this person may connect or disconnect it — administrators only. */
  canManage: z.boolean(),
  /**
   * Whether the workspace granted direct messages (`im:write`, PRD 56). False
   * for an install from before it was asked for, until it is connected again.
   */
  canSendDms: z.boolean(),
  /** This person's own Slack — what sending AS them needs (PRD 56 §6). */
  me: z.object({ connected: z.boolean() }),
});
export type SlackState = z.infer<typeof slackState>;

/** A channel the notetaker's workspace can post to. */
export const slackChannel = z.object({
  id: z.string(),
  name: z.string(),
  isPrivate: z.boolean(),
  /** Members, when Slack reports it — a hint about where a post will land. */
  members: z.number().int().nullable(),
});
export type SlackChannel = z.infer<typeof slackChannel>;

/**
 * What a meeting would post, before anybody presses anything.
 *
 * ⚠ The owners who cannot be tagged are named HERE, not discovered after the
 * message is out: "a person with no Slack account is named in plain text, and
 * the composer says so *before* the post goes out — never a silently dropped
 * mention" (PRD 33 §3).
 */
export const slackDraft = z.object({
  /** The series' channel, when it has one — a suggestion, never a decision. */
  suggestedChannelId: z.string().nullable(),
  suggestedChannelName: z.string().nullable(),
  /**
   * The channels this meeting is already in, one message each.
   *
   * ⚠ Per channel, because a person chooses differently per room: the one that
   * needs the decisions is not always the one that needs the promises. Posting
   * to a channel that is here updates its message; to one that is not, writes
   * a new one.
   */
  posted: z.array(
    z.object({
      channelId: z.string(),
      channelName: z.string().nullable(),
      ts: z.string(),
      commitments: z.number().int(),
      decisions: z.number().int(),
      at: isoDate,
    }),
  ),
  commitments: z.array(
    z.object({
      id: uuid,
      text: z.string(),
      ownerEmail: z.string().nullable(),
      ownerName: z.string().nullable(),
      /** False when nobody in the Slack workspace has that address. */
      taggable: z.boolean(),
      /** The channels whose message already carries it. */
      postedIn: z.array(z.string()),
      /** Whose direct messages already carry it, by address (PRD 56). */
      sentTo: z.array(z.string()),
    }),
  ),
  decisions: z.array(z.object({ id: uuid, text: z.string(), postedIn: z.array(z.string()) })),
  /** The sender's own address — whose actions ride along in every DM. */
  me: z.string().nullable(),
  /** Whether this workspace allows direct messages from the bot yet (PRD 56). */
  dmReady: z.boolean(),
  /** Whether the sender has connected their own Slack, to send as themselves. */
  meReady: z.boolean(),
  /** The direct messages this meeting is already in, one per person. */
  dms: z.array(
    z.object({
      channelId: z.string(),
      recipientEmail: z.string().nullable(),
      recipientName: z.string().nullable(),
      commitments: z.number().int(),
      at: isoDate,
      /** Sent as the sender, into their own DM — rather than by the bot. */
      asUser: z.boolean(),
    }),
  ),
});
export type SlackDraft = z.infer<typeof slackDraft>;

/**
 * What to send as direct messages (PRD 56): the chosen actions, and nothing
 * about who — that follows from whose they are.
 *
 * ⚠ Vladimir, 2026-09-23: *"send to the person only the actions that that user
 * owns … BUT if I select actions where I am the owner they shall be sent to
 * each user as well, so they can track how I am progressing"*. Each other
 * owner gets their own ones and the sender's; nobody gets anybody else's.
 */
export const slackDmBody = z.object({
  commitmentIds: z.array(z.string().uuid()).min(1).max(50),
  /** What was decided — sent to every recipient, since it is the meeting's (PRD 56 §7). */
  decisionIds: z.array(z.string().uuid()).max(50).default([]),
  /**
   * Who it comes from (PRD 56 §6): `me` — the sender, in their own DM with
   * each person, which needs their own Slack connected; `bot` — Ptitim, in the
   * person's conversation with the app. Vladimir: *"leave both options"*.
   */
  as: z.enum(['me', 'bot']).default('bot'),
});
export type SlackDmBody = z.infer<typeof slackDmBody>;
/** The same, before its defaults are filled in — what a caller inside the server passes. */
export type SlackDmInput = z.input<typeof slackDmBody>;

export const slackDmsSent = z.object({
  as: z.enum(['me', 'bot']),
  /** One per person reached: their name, how many of theirs, and whether it edited an earlier DM. */
  sent: z.array(z.object({ name: z.string(), yours: z.number().int(), updated: z.boolean() })),
  /** Owners Slack has no account for, or would not open a conversation with. */
  unreachable: z.array(z.string()),
  /** How many of the sender's own rode along in each. */
  mine: z.number().int(),
  /** Chosen actions with nobody's address on them — sent to nobody. */
  unowned: z.number().int(),
});
export type SlackDmsSent = z.infer<typeof slackDmsSent>;

/**
 * A meeting's note — the full one, or one of its write-ups — to one person's
 * Slack, from the sender's own Slack and never the bot (2026-10-08, Aleksandr:
 * notes straight into a direct conversation, not through Ptitim's app).
 */
export const slackNoteDmBody = z.object({
  to: z.string().trim().toLowerCase().email().max(320),
  /** One of the meeting's write-ups; absent: the full note. */
  writeupId: uuid.optional(),
});
export type SlackNoteDmBody = z.infer<typeof slackNoteDmBody>;

export const slackNoteDmSent = z.object({ name: z.string() });
export type SlackNoteDmSent = z.infer<typeof slackNoteDmSent>;

/**
 * What to post.
 *
 * ⚠ Ids, never "all". Vladimir, 2026-09-21: *"I need to be able to select what
 * commitments are added to the message to slack and which are not, sometimes I
 * don't want to send them all"*. An empty list is a refusal, not a message.
 */
export const slackPostBody = z.object({
  channelId: z.string().min(1).max(64),
  /** What it is called, for the record — Slack only ever hands back an id. */
  channelName: z.string().min(1).max(120).optional(),
  commitmentIds: z.array(z.string().uuid()).max(50),
  decisionIds: z.array(z.string().uuid()).max(50),
});
export type SlackPostBody = z.infer<typeof slackPostBody>;

export const slackPosted = z.object({
  channelId: z.string(),
  ts: z.string(),
  /** Whether this edited the channel's message or wrote a new one. */
  updated: z.boolean(),
  /** How many of each went in, for the sentence the app shows afterwards. */
  commitments: z.number().int(),
  decisions: z.number().int(),
  /** Named in plain text because Slack has no account for them. */
  untagged: z.array(z.string()),
});
export type SlackPosted = z.infer<typeof slackPosted>;
